import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { useT } from "../i18n";
import { documentApi } from "../services/document.service";
import { canAccessDeals } from "../utils/permissions";
import NotificationBell from "./NotificationBell";
import { useWhatsNewContext } from "../hooks/WhatsNewContext";

// A small green dot shown next to a nav item when it has unseen new content.
const NewDot: React.FC<{ show?: boolean }> = ({ show }) =>
  show ? (
    <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-emerald-400" />
  ) : null;

const Sidebar: React.FC = () => {
  const { logout, user } = useAuth();
  const t = useT();
  const { newKeys } = useWhatsNewContext();
  const [documentUrl, setDocumentUrl] = useState(
    "https://www.notion.so/hiluxia/Luxia-User-Manual-11a00530bce98032b9f0db4a65ceb3ed"
  );

  useEffect(() => {
    const fetchDocumentUrl = async () => {
      try {
        const response = await documentApi.getAllDocuments();
        if (response.data && response.data.length > 0) {
          setDocumentUrl(response.data[0].url);
        }
      } catch (error) {
        console.error("Error fetching document URL:", error);
        // Keep the default URL if fetch fails
      }
    };

    fetchDocumentUrl();
  }, []);

  const handleLogout = () => {
    logout();
  };

  return (
    <aside className="w-72 min-w-[18rem] max-w-[18rem] bg-gray-900 shadow-sm border-r border-gray-700 flex flex-col h-screen fixed top-0 left-0 z-30">
      <div
        id="sidebar-header"
        className="p-6 flex items-center justify-between border-b border-gray-700 h-20 bg-gray-900"
      >
        <div className="flex items-center gap-3">
          <img
            src={process.env.PUBLIC_URL + "/luxia.svg"}
            alt={t("layout.logoAlt")}
            className="w-10 h-10 object-contain bg-transparent rounded-lg"
          />
          <span className="text-xl font-semibold text-white">
            {t("layout.portalName")}
          </span>
        </div>
        <NotificationBell />
      </div>
      <nav className="flex-1 p-4">
        <ul className="space-y-1">
          <li>
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg ${
                  isActive
                    ? "bg-white text-gray-900"
                    : "hover:bg-gray-800 text-gray-300"
                } cursor-pointer font-medium`
              }
            >
              {({ isActive }) => (
                <>
                  <i
                    className={`fa-solid fa-house w-5 text-center text-base ${
                      isActive ? "text-gray-900" : "text-gray-300"
                    }`}
                  ></i>
                  <span>{t("layout.sidebar.dashboard")}</span>
                </>
              )}
            </NavLink>
          </li>
          {canAccessDeals(user) && (
            <li>
              <NavLink
                to="/deals"
                className={({ isActive }) =>
                  `sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg ${
                    isActive
                      ? "bg-white text-gray-900"
                      : "hover:bg-gray-800 text-gray-300"
                  } cursor-pointer font-medium`
                }
              >
                {({ isActive }) => (
                  <>
                    <i
                      className={`fa-solid fa-tags w-5 text-center text-base ${
                        isActive ? "text-gray-900" : "text-gray-300"
                      }`}
                    ></i>
                    <span>{t("layout.sidebar.deals")}</span>
                  </>
                )}
              </NavLink>
            </li>
          )}
          <li>
            <NavLink
              to="/release"
              className={({ isActive }) =>
                `sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg ${
                  isActive
                    ? "bg-white text-gray-900"
                    : "hover:bg-gray-800 text-gray-300"
                } cursor-pointer font-medium`
              }
            >
              {({ isActive }) => (
                <>
                  <i
                    className={`fa-solid fa-rocket w-5 text-center mr-2 text-base ${
                      isActive ? "text-gray-900" : "text-gray-300"
                    }`}
                  ></i>
                  <span>{t("layout.sidebar.releases")}</span>
                  <NewDot show={newKeys.has("release")} />
                </>
              )}
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/product-sheet"
              className={({ isActive }) =>
                `sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg ${
                  isActive
                    ? "bg-white text-gray-900"
                    : "hover:bg-gray-800 text-gray-300"
                } cursor-pointer font-medium`
              }
            >
              {({ isActive }) => (
                <>
                  <i
                    className={`fa-solid fa-file-invoice w-5 text-center text-base ${
                      isActive ? "text-gray-900" : "text-gray-300"
                    }`}
                  ></i>
                  <span>{t("layout.sidebar.productSheets")}</span>
                  <NewDot show={newKeys.has("sheet")} />
                </>
              )}
            </NavLink>
          </li>
          <li>
            <a
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-gray-800 text-gray-300 cursor-pointer font-medium"
            >
              <i className="fa-solid fa-file-lines w-5 text-center text-base text-gray-300"></i>
              <span>{t("layout.sidebar.documents")}</span>
              <NewDot show={newKeys.has("document")} />
            </a>
          </li>
          <li>
            <NavLink
              to="/brand-guide"
              className={({ isActive }) =>
                `sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg ${
                  isActive
                    ? "bg-white text-gray-900"
                    : "hover:bg-gray-800 text-gray-300"
                } cursor-pointer font-medium`
              }
            >
              {({ isActive }) => (
                <>
                  <i
                    className={`fa-solid fa-copyright w-5 text-center text-base ${
                      isActive ? "text-gray-900" : "text-gray-300"
                    }`}
                  ></i>
                  <span>{t("layout.sidebar.brandGuide")}</span>
                  <NewDot show={newKeys.has("brand")} />
                </>
              )}
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/graphics"
              className={({ isActive }) =>
                `sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg ${
                  isActive
                    ? "bg-white text-gray-900"
                    : "hover:bg-gray-800 text-gray-300"
                } cursor-pointer font-medium`
              }
            >
              {({ isActive }) => (
                <>
                  <i
                    className={`fa-solid fa-image w-5 text-center text-base ${
                      isActive ? "text-gray-900" : "text-gray-300"
                    }`}
                  ></i>
                  <span>{t("layout.sidebar.graphics")}</span>
                  <NewDot show={newKeys.has("graphics")} />
                </>
              )}
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/webinar"
              className={({ isActive }) =>
                `sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg ${
                  isActive
                    ? "bg-white text-gray-900"
                    : "hover:bg-gray-800 text-gray-300"
                } cursor-pointer font-medium`
              }
            >
              {({ isActive }) => (
                <>
                  <i
                    className={`fa-solid fa-video w-5 text-center text-base ${
                      isActive ? "text-gray-900" : "text-gray-300"
                    }`}
                  ></i>
                  <span>{t("layout.sidebar.webinar")}</span>
                  <NewDot show={newKeys.has("webinar")} />
                </>
              )}
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/academy"
              className={({ isActive }) =>
                `sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg ${
                  isActive
                    ? "bg-white text-gray-900"
                    : "hover:bg-gray-800 text-gray-300"
                } cursor-pointer font-medium`
              }
            >
              {({ isActive }) => (
                <>
                  <i
                    className={`fa-solid fa-graduation-cap w-5 text-center text-base ${
                      isActive ? "text-gray-900" : "text-gray-300"
                    }`}
                  ></i>
                  <span>{t("layout.sidebar.academy")}</span>
                  <NewDot show={newKeys.has("academy")} />
                </>
              )}
            </NavLink>
          </li>
        </ul>
      </nav>
      <div className="p-4 border-t border-gray-700 flex flex-col gap-2">
        <LanguageSwitcher />
        <span className="sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-gray-800 text-gray-300 cursor-pointer font-medium">
          <i className="fa-solid fa-user w-5 text-center text-base text-gray-300"></i>
          <span>{user?.username || t("layout.sidebar.userProfile")}</span>
        </span>
        <span
          onClick={handleLogout}
          className="sidebar-item flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-gray-800 text-gray-300 cursor-pointer font-medium"
        >
          <i className="fa-solid fa-arrow-right-from-bracket w-5 text-center text-base text-gray-300"></i>
          <span>{t("layout.sidebar.logout")}</span>
        </span>
      </div>
    </aside>
  );
};

export default Sidebar;
